import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

const FEATURE_PLANS_ENUM = {
  LITE_TO_PREMIUM: 'lite-to-premium',
  LEGACY: 'legacy',
  PREMIUM: 'premium',
  STANDARD: 'standard',
  LEGACY_TO_STANDARD: 'legacy-to-standard',
  LEGACY_TO_PREMIUM: 'legacy-to-premium',
};

const getFeatures = (plan) => {
  // Initialize an empty array to store item numbers to get values from translations
  let itemNumbers = [];

  // Determine the item numbers based on the provided plan
  switch (plan) {
    case FEATURE_PLANS_ENUM.LITE_TO_PREMIUM:
    case FEATURE_PLANS_ENUM.PREMIUM:
    case FEATURE_PLANS_ENUM.STANDARD:
    case FEATURE_PLANS_ENUM.LEGACY_TO_STANDARD:
    case FEATURE_PLANS_ENUM.LEGACY_TO_PREMIUM:
      itemNumbers = [1, 2, 3];
      break;
    case FEATURE_PLANS_ENUM.LEGACY:
      itemNumbers = [1, 2, 3, 4, 5];
      break;
    default:
      // For unsupported plans, return an empty array
      return [];
  }

  // Map item numbers to feature objects with localized text
  return itemNumbers.map((itemNumber) => {
    const isLegacyToV21 = [FEATURE_PLANS_ENUM.LEGACY_TO_STANDARD, FEATURE_PLANS_ENUM.LEGACY_TO_PREMIUM].includes(plan);

    if (isLegacyToV21) {
      const isToStandard = plan === FEATURE_PLANS_ENUM.LEGACY_TO_STANDARD;

      return {
        text: (
          <Trans i18nKey={`upgrade-popup.legacy-popup.${isToStandard ? 'standard' : 'premium'}-features.${itemNumber}`}>
            <strong />
          </Trans>
        ),
      };
    }

    return {
      text: (
        <Trans i18nKey={`upgrade-popup.${plan}.item${itemNumber}`}>
          <strong />
        </Trans>
      ),
    };
  });
};

const useUpgradeStepContentCopy = ({
  isPremium,
  isLiteToPremium,
  isLegacy,
  recommendedUpgradePlanPromotion,
  isLegacyToStandard,
  isLegacyToPremium,
}) => {
  const { t } = useTranslation();

  const featureList = useMemo(() => {
    if (isLiteToPremium) return getFeatures(FEATURE_PLANS_ENUM.LITE_TO_PREMIUM);

    if (isLegacy) return getFeatures(FEATURE_PLANS_ENUM.LEGACY);

    if (isPremium) return getFeatures(FEATURE_PLANS_ENUM.PREMIUM);

    if (isLegacyToStandard) return getFeatures(FEATURE_PLANS_ENUM.LEGACY_TO_STANDARD);

    if (isLegacyToPremium) return getFeatures(FEATURE_PLANS_ENUM.LEGACY_TO_PREMIUM);

    return getFeatures(FEATURE_PLANS_ENUM.STANDARD);
  }, [isLiteToPremium, isPremium, isLegacy, isLegacyToStandard, isLegacyToPremium, t]);

  const title = useMemo(() => {
    const getPromoTitle = () => {
      return t('upgrade-popup.legacy-popup.header', {
        percent: recommendedUpgradePlanPromotion?.promotionAmount,
        count: recommendedUpgradePlanPromotion?.promotionDuration,
      });
    };

    const getTitle = (baseTitle) => {
      return recommendedUpgradePlanPromotion?.promotionCode ? `${baseTitle}. ${getPromoTitle()}` : baseTitle;
    };

    const planType = isPremium || isLegacyToPremium ? 'premium' : 'standard';

    if (isLegacyToStandard || isLegacyToPremium) {
      const legacyToV21title = t(`upgrade-popup.legacy-popup.header-${planType}`);

      return getTitle(legacyToV21title);
    }

    const dialogTitle = t(`upgrade-popup.${planType}.title`);

    return getTitle(dialogTitle);
  }, [isPremium, recommendedUpgradePlanPromotion]);

  const description = useMemo(() => {
    let planType = isPremium || isLegacyToPremium ? 'premium' : 'standard';

    if (isLegacyToStandard || isLegacyToPremium) {
      return t(`upgrade-popup.legacy-popup.body-${planType}`);
    }

    if (isLegacy) {
      planType = 'legacy';
    }

    return t(`upgrade-popup.${planType}.description`);
  }, [isPremium, isLegacy, isLegacyToStandard, isLegacyToPremium]);

  return {
    featureList,
    title,
    description,
  };
};

export default useUpgradeStepContentCopy;
